import { useState, useRef, useEffect } from "react";
import { CREATE_TSP_TOKEN, getToken, SEND_OTP_CODE, getOTPSendSuccess, getPlatformForSms } from "apps/login/util/mutations";
import { useMutation } from "@apollo/react-hooks";
import FeedbackBanner from "shared/util/FeedbackBanner";
import { SEND_CODE_FAILED_ERROR_MESSAGE } from "shared/constants/sms";

export const showSendCodeFeedbackBanner = (message = SEND_CODE_FAILED_ERROR_MESSAGE) =>
	FeedbackBanner.open({
		message,
		type: "error",
		timeout: 3000,
	});

// codes can only be sent at most once per 20 seconds
const SEND_CODE_WAIT = 20 * 1000;

const SEND_CODE_ERROR = new Error(SEND_CODE_FAILED_ERROR_MESSAGE);

const useSendCode = ({ disableLeadingSend = false } = {}) => {
	const [createTspToken, createTspTokenResult] = useMutation(CREATE_TSP_TOKEN);
	const [sendOTPCode, sendOTPCodeResult] = useMutation(SEND_OTP_CODE);

	// use disableLeadingSend for sms screens where the code has already been sent
	// since codes cannot be successfully re-sent within 20 seconds
	const [canSendCode, setCanSendCode] = useState(!disableLeadingSend);
	const [isSendingCode, setIsSendingCode] = useState(false);
	const timer = useRef(null);

	useEffect(() => {
		if (disableLeadingSend) {
			timer.current = setTimeout(() => setCanSendCode(true), SEND_CODE_WAIT);
		}

		return () => clearTimeout(timer.current);
	}, [disableLeadingSend]);

	const sendCode = (phoneNumber) => {
		// make sure users cannot call this function more than once per 20 seconds
		setCanSendCode(false);
		timer.current = setTimeout(() => setCanSendCode(true), SEND_CODE_WAIT);

		if (!canSendCode) {
			return Promise.reject(SEND_CODE_ERROR);
		}

		setIsSendingCode(true);

		return createTspToken()
			.then((res) => {
				const tspAccessToken = getToken(res);

				if (!tspAccessToken) {
					setIsSendingCode(false);
					return Promise.reject(SEND_CODE_ERROR);
				}

				return tspAccessToken;
			})
			.then((tspAccessToken) => {
				const otpVariables = {
					input: {
						tspAccessToken,
						phoneNumber,
						platform: getPlatformForSms(),
					},
				};

				return sendOTPCode({ variables: otpVariables })
					.then((res) => {
						setIsSendingCode(false);
						const success = getOTPSendSuccess(res);

						if (!success) {
							return Promise.reject(SEND_CODE_ERROR);
						}

						return true;
					});
			})
			.catch((err) => {
				setIsSendingCode(false);
				return Promise.reject(err);
			});
	};

	return { sendCode, createTspTokenResult, sendOTPCodeResult, canSendCode, isSendingCode };
};

export default useSendCode;



// WEBPACK FOOTER //
// ./src/shared/hooks/useSendCode.js